import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import { 
    FaTimes, FaChevronLeft, FaChevronRight, FaSearchPlus, FaSearchMinus,
    FaFont, FaPen, FaSignature, FaSquare, FaCircle, FaCheck, FaTimes as FaCross,
    FaSave, FaHistory, FaMousePointer, FaTrash, FaLayerGroup, FaUndo, FaCaretUp,
    FaBold, FaItalic, FaUnderline, FaPalette, FaPlus, FaMinus, FaHighlighter,
    FaEraser, FaUpload
} from 'react-icons/fa';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/PdfAnnotationModal.scss';
import * as Sentry from '@sentry/react';
// Bundle the PDF.js worker locally (served from our own origin) instead of a CDN.
// Vite's `?url` suffix emits the asset and returns its hashed URL. The worker version
// is therefore always identical to the installed pdfjs-dist (matching react-pdf's
// bundled copy), and rendering no longer depends on external CDN availability,
// corporate firewalls, or network latency — the root cause of the previously
// "temperamental" PDF viewer that rarely loaded.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';



// Configure PDF.js worker — use the locally-bundled, version-matched worker.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;




/**
 * PdfAnnotationModal Component
 * Full-featured PDF viewer with annotation capabilities including text, shapes, signatures, and drawing
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
  * @param {Object} props.attachment - Attachment object containing PDF information
  * @param {boolean} [props.contractDocumentMode] - When true, uses the ServiceProviders annotation API
  *   instead of the PdfAnnotation API (for ISP contract documents).
  */
 const PdfAnnotationModal = ({ isOpen, onClose, attachment, contractDocumentMode = false }) => {
 const { apiFetch, user, loading: authLoading } = useAuth();

 // Authentication check - only allow authenticated users
 const isAuthenticated = !!user?.token;

 // Resolve API base URLs depending on whether this is a contract document annotation.
 const annotationApiBase = contractDocumentMode
     ? `/api/ServiceProviders/documents/${attachment?.attachmentId}`
     : null;
 const getVersionCurrentUrl = contractDocumentMode
     ? `${annotationApiBase}/annotation/current`
     : `/api/PdfAnnotation/versions/attachment/${attachment?.attachmentId}/current`;
 const getVersionsUrl = contractDocumentMode
     ? `${annotationApiBase}/annotations`
     : `/api/PdfAnnotation/versions/attachment/${attachment?.attachmentId}`;
 const createVersionUrl = contractDocumentMode
     ? `${annotationApiBase}/annotation`
     : '/api/PdfAnnotation/versions';
 const getAuditUrl = contractDocumentMode
     ? `${annotationApiBase}/annotation/audit`
     : `/api/PdfAnnotation/audit/attachment/${attachment?.attachmentId}`;

 const canvasRef = useRef(null);
 const [numPages, setNumPages] = useState(0);
 const [currentPage, setCurrentPage] = useState(1);
 const [scale, setScale] = useState(1.0);
 const [pageDimensions, setPageDimensions] = useState({ width: 0, height: 0 });
 const [currentVersion, setCurrentVersion] = useState(null);
const [versions, setVersions] = useState([]);
const [annotations, setAnnotations] = useState([]);
const [selectedTool, setSelectedTool] = useState('select'); // select, text, draw, signature, rectangle, circle, check, cross, caret, highlight, redact
const [isDrawing, setIsDrawing] = useState(false);
const [drawingPath, setDrawingPath] = useState([]);
const [userSignature, setUserSignature] = useState(null);
const [auditTrail, setAuditTrail] = useState([]);
const [showAuditTrail, setShowAuditTrail] = useState(false);
const [loading, setLoading] = useState(true);
const [showTextInputModal, setShowTextInputModal] = useState(false);
const [textInputValue, setTextInputValue] = useState('');
const [textInputCallback, setTextInputCallback] = useState(null);
const [showVersionModal, setShowVersionModal] = useState(false);
const [versionDescription, setVersionDescription] = useState('');

// Authenticated PDF blob URL state.
// The download endpoint requires a JWT Bearer token.  react-pdf's <Document> component
// cannot inject custom request headers when given a plain URL string, so we fetch the PDF
// bytes ourselves via apiFetch (which adds the Authorization header automatically) and
// convert the response to an in-memory Blob URL that react-pdf can load without auth.
const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
const [pdfLoadError, setPdfLoadError] = useState(null);

// Counter incremented by the "Try again" button to force a re-fetch / re-render of the
// PDF after a transient failure (network blip, expired token refreshed, worker hiccup),
// without requiring the user to close and reopen the modal.
const [pdfReloadKey, setPdfReloadKey] = useState(0);

// Track which attachment ID the current blob URL belongs to so we can skip
// re-fetching when the modal is reopened for the same attachment.
const loadedAttachmentIdRef = useRef(null);
    
// Signature creation modal state
const [showSignatureModal, setShowSignatureModal] = useState(false);
const [signatureDrawing, setSignatureDrawing] = useState([]);
const [isDrawingSignature, setIsDrawingSignature] = useState(false);
const signatureCanvasRef = useRef(null);
const signatureFileInputRef = useRef(null);
    
// Text selection for redaction
const [textSelection, setTextSelection] = useState(null);

// Tool colors and options
const [selectedColor, setSelectedColor] = useState(() => {
        // Load saved color from localStorage, default to black
        return localStorage.getItem('pdfAnnotation_selectedColor') || '#000000';
    });
    const [fontSize, setFontSize] = useState(14);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showVersionSelector, setShowVersionSelector] = useState(false);
    
    // Drag state for moving annotations
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [draggedAnnotation, setDraggedAnnotation] = useState(null);
    const annotationLayerRef = useRef(null);
    const pdfPageWrapperRef = useRef(null);
    
    // Cursor preview state for shape tools
    const [mousePosition, setMousePosition] = useState(null);
    
    // Resize state
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [resizeStart, setResizeStart] = useState(null);
    
    // Highlighted annotation from audit trail (view only, not editable)
    const [highlightedAnnotationId, setHighlightedAnnotationId] = useState(null);
    
    // Filename rename state
    const [isRenaming, setIsRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [currentFileName, setCurrentFileName] = useState('');
    const renameInputRef = useRef(null);
    
    // Default sizes for click-to-place shapes
    const defaultShapeSizes = {
        rectangle: { width: 100, height: 60 },
        circle: { width: 60, height: 60 },
        check: { width: 30, height: 30 },
        cross: { width: 30, height: 30 },
        caret: { width: 24, height: 24 },
        highlight: { width: 200, height: 24 },
        signature: { width: 150, height: 60 }
        };



    /**
     * Load PDF document and its versions/annotations
     */
    const loadPdfData = async () => {
        try {
            setLoading(true);

            // Try to load current version, create if none exists
            let currentVer = null;
            try {
                const versionResponse = await apiFetch(getVersionCurrentUrl);
                
                if (versionResponse.ok) {
                    const versionData = await versionResponse.json();
                    if (versionData.success && versionData.data) {
                        currentVer = versionData.data;
                    }
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                // Current version doesn't exist, will create below
                if (process.env.NODE_ENV === 'development') {
                    console.log('No current version found, will create initial version');
                }
            }

            // Create first version if none exists
            if (!currentVer) {
                try {
                    const createBody = contractDocumentMode
                        ? { versionDescription: 'Initial version' }
                        : { attachmentId: attachment.attachmentId, versionDescription: 'Initial version' };

                    const createResponse = await apiFetch(createVersionUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(createBody)
                    });
                    
                    if (createResponse.ok) {
                        const createData = await createResponse.json();
                        if (createData.success && createData.data) {
                            currentVer = createData.data;
                        }
                    }
                } catch (createError) {
                    Sentry.captureException(createError);
                    if (process.env.NODE_ENV === 'development') {
                        console.error('Error creating initial version:', createError);
                    }
                    throw new Error('Failed to create initial PDF version', { cause: createError });
                }
            }

            if (currentVer) {
                setCurrentVersion(currentVer);
                await loadAnnotations(currentVer.pdfVersionId);
            }

            // Load all versions
            await loadVersions();

            // Load audit trail
            await loadAuditTrail();

        } catch (error) {
            Sentry.captureException(error);
            // Error loading PDF data - log to console in development
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading PDF data:', error);
            }
            toast.error('Failed to load PDF data. Please try refreshing.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load annotations for a specific version
     */
    const loadAnnotations = useCallback(async (versionId) => {
        try {
            const response = await apiFetch(`/api/PdfAnnotation/annotations/version/${versionId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setAnnotations(data.data);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            // Error loading annotations - silently fail as this is not critical
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading annotations:', error);
            }
        }
    }, [apiFetch]);

    /**
     * Load all versions for this PDF
     */
    const loadVersions = async () => {
        try {
            const response = await apiFetch(getVersionsUrl);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setVersions(data.data);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            // Error loading versions - silently fail as this is not critical
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading versions:', error);
            }
        }
    };

    /**
     * Load audit trail
     */
    const loadAuditTrail = useCallback(async () => {
        try {
            const response = await apiFetch(getAuditUrl);
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setAuditTrail(data.data);
                }
            }
        } catch (error) {
            Sentry.captureException(error);
            // Error loading audit trail - silently fail as this is not critical
            if (process.env.NODE_ENV === 'development') {
                console.error('Error loading audit trail:', error);
            }
        }
    }, [apiFetch, getAuditUrl]);

    /**
     * Load user's signature
     */
    const loadUserSignature = async () => {
        try {
            const response = await apiFetch('/api/User/signature');
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setUserSignature(data.data.signatureData);
                }
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            // No signature exists yet, that's okay - not an error condition
        }
    };

    /**
     * Wraps the parent's onClose callback to ensure the authenticated PDF Blob URL is
     * revoked before the modal is unmounted, preventing memory leaks.
     */
    const handleClose = useCallback(() => {
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
            loadedAttachmentIdRef.current = null;
        }
        onClose();
    }, [onClose, pdfBlobUrl]);

    /**
     * Fetches the PDF file bytes via the authenticated apiFetch wrapper and stores
     * the result as an in-memory Blob URL.
     *
     * Background: the download endpoint (/api/ActionItems/attachments/{id}/download)
     * requires a JWT Bearer token in the Authorization header.  react-pdf's <Document>
     * component cannot attach custom headers when given a plain URL string, which causes
     * HTTP 401 errors.  By performing the fetch here with apiFetch (which injects the
     * token automatically) and passing the resulting Blob URL to <Document>, we can load
     * any protected PDF without exposing the JWT in the URL query string.
     *
     * The Blob URL is revoked on modal close (see onClose handler) to prevent memory leaks.
     */
    const loadPdfFile = async () => {
        // Skip fetching if we already have a valid Blob URL for this exact attachment,
        // e.g. when the modal is closed and reopened without changing the attachment.
        if (
            pdfBlobUrl &&
            loadedAttachmentIdRef.current === attachment?.attachmentId
        ) {
            return;
        }

        // Clear any previous state for this attachment
        setPdfLoadError(null);

        // Release any previously allocated blob URL to avoid memory leaks
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
            loadedAttachmentIdRef.current = null;
        }

        if (!attachment?.downloadUrl) {
            setPdfLoadError('No download URL available for this attachment.');
            return;
        }

        try {
            const response = await apiFetch(attachment.downloadUrl);
            if (!response.ok) {
                const errorText = `Failed to load PDF (HTTP ${response.status}).`;
                setPdfLoadError(errorText);
                return;
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(objectUrl);
            loadedAttachmentIdRef.current = attachment.attachmentId;
        } catch (err) {
            Sentry.captureException(err);
            setPdfLoadError('Could not load the PDF file. Please try again.');
            if (process.env.NODE_ENV === 'development') {
                console.error('[PdfAnnotationModal] loadPdfFile error:', err);
            }
        }
    };

    useEffect(() => {
        (async () => {
            if (isOpen && attachment) {
                // Load the PDF bytes, annotation data, and the user's signature independently.
                // Each is wrapped so that a failure in one does not abort the others — e.g. a
                // transient annotation-API error must not prevent the PDF itself from rendering.
                try {
                    await loadPdfFile();
                } catch (err) {
                    Sentry.captureException(err);
                    setPdfLoadError('Could not load the PDF file. Please try again.');
                }

                try {
                    await loadPdfData();
                } catch (err) {
                    Sentry.captureException(err);
                }

                try {
                    await loadUserSignature();
                } catch (err) {
                    Sentry.captureException(err);
                }

                setCurrentFileName(attachment.fileName);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, attachment, pdfReloadKey]);

    /**
     * Retry loading the PDF after a failure, without closing the modal.
     * Clears the previous error/blob and bumps the reload key so the load effect re-runs.
     */
    const handleRetryPdf = useCallback(() => {
        setPdfLoadError(null);
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
        }
        loadedAttachmentIdRef.current = null;
        setPdfReloadKey(k => k + 1);
    }, [pdfBlobUrl]);

    /**
     * Handle PDF document load
     */
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        // A successful render clears any stale error banner from a previous attempt.
        setPdfLoadError(null);
    };

    /**
     * Handle a failure inside react-pdf / PDF.js itself (worker init, parsing, corrupt
     * file). This is distinct from the authenticated-fetch failures handled in loadPdfFile,
     * and was previously swallowed — a primary reason the viewer appeared to "rarely work".
     */
    const onDocumentLoadError = (error) => {
        Sentry.captureException(error);
        if (process.env.NODE_ENV === 'development') {
            console.error('[PdfAnnotationModal] Document load error:', error);
        }
        setPdfLoadError('The PDF could not be displayed. It may be corrupt or still uploading.');
    };

    /**
     * Handle page render success - get the actual rendered dimensions
     */
    const onPageRenderSuccess = (page) => {
        const { width, height } = page;
        setPageDimensions({ width, height });
    };

    /**
     * Handle page navigation
     */
    const goToPrevPage = () => {
        setCurrentPage(page => Math.max(1, page - 1));
    };

    const goToNextPage = useCallback(() => {
        setCurrentPage(page => Math.min(numPages, page + 1));
    }, [numPages]);

    /**
     * Handle color change and persist to localStorage
     */
    const handleColorChange = (newColor) => {
        setSelectedColor(newColor);
        localStorage.setItem('pdfAnnotation_selectedColor', newColor);
    };

    /**
     * Handle zoom
     */
    const zoomIn = () => {
        setScale(scale => Math.min(3.0, scale + 0.25));
    };

    const zoomOut = () => {
        setScale(scale => Math.max(0.5, scale - 0.25));
    };

    /**
     * Save annotation to backend
     */
    const saveAnnotation = async (annotationData) => {
        try {
            if (!currentVersion) {
                toast.error('No PDF version available');
                return;
            }

            const response = await apiFetch('/api/PdfAnnotation/annotations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    attachmentId: attachment.attachmentId,
                    pdfVersionId: currentVersion.pdfVersionId,
                    annotationType: annotationData.type,
                    pageNumber: currentPage,
                    positionX: annotationData.x,
                    positionY: annotationData.y,
                    width: annotationData.width,
                    height: annotationData.height,
                    textContent: annotationData.text || null,
                    annotationData: annotationData.data ? JSON.stringify(annotationData.data) : null,
                    fontFamily: annotationData.fontFamily || 'Arial',
                    fontSize: annotationData.fontSize || fontSize,
                    color: annotationData.color || selectedColor,
                    zIndex: annotations.length
                })
            });

            const data = await response.json();
            if (data.success) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
                toast.success('Annotation saved');
            } else {
                toast.error('Failed to save annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error saving annotation:', error);
            }
            toast.error('Failed to save annotation');
        }
    };

    /**
     * Update annotation position after dragging
     */
    const updateAnnotationPosition = async (annotation) => {
        try {
            setIsSaving(true);
            const response = await apiFetch(`/api/PdfAnnotation/annotations/${annotation.annotationId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    positionX: annotation.positionX,
                    positionY: annotation.positionY
                })
            });

            if (response.ok) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
                // Update selected annotation with new position
                setSelectedAnnotation(prev => prev ? {
                    ...prev,
                    positionX: annotation.positionX,
                    positionY: annotation.positionY
                } : null);
            } else {
                toast.error('Failed to move annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error updating annotation position:', error);
            }
            toast.error('Failed to move annotation');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Get mouse coordinates relative to the annotation layer
     */
    const getMousePosition = (e) => {
        const annotationLayer = annotationLayerRef.current;
        if (!annotationLayer) return null;

        const rect = annotationLayer.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        return { x, y };
    };

    /**
     * Get mouse coordinates relative to the pdf page wrapper (for cursor preview)
     */
    const getMousePositionFromWrapper = (e) => {
        const wrapper = pdfPageWrapperRef.current;
        if (!wrapper) return null;

        const rect = wrapper.getBoundingClientRect();
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        return { x, y };
    };

    /**
     * Handle mouse move on wrapper for cursor preview tracking
     */
    const handleWrapperMouseMove = (e) => {
        const pos = getMousePositionFromWrapper(e);
        if (pos) {
            setMousePosition(pos);
        }
    };

    /**
     * Handle mouse events for drawing/placing annotations
     */
    const handleMouseDown = (e) => {
        // Ignore right-click - let contextMenu handler deal with it
        if (e.button === 2) return;
        
        const pos = getMousePosition(e);
        if (!pos) return;
        const { x, y } = pos;

        // Handle resize start
        if (isResizing) return;

        // Handle select tool - start drag if clicking on selected annotation
        if (selectedTool === 'select') {
            if (selectedAnnotation) {
                // Check if clicking within the selected annotation bounds
                const ann = selectedAnnotation;
                if (x >= ann.positionX && x <= ann.positionX + ann.width &&
                    y >= ann.positionY && y <= ann.positionY + ann.height) {
                    setIsDragging(true);
                    setDragStart({ x, y });
                    setDraggedAnnotation({
                        ...ann,
                        originalX: ann.positionX,
                        originalY: ann.positionY
                    });
                    e.preventDefault();
                }
            }
            return;
        }

        if (selectedTool === 'draw' || selectedTool === 'highlight') {
            setIsDrawing(true);
            setDrawingPath([{ x, y }]);
        } else if (selectedTool === 'text') {
            setTextInputValue('');
            setTextInputCallback(() => (text) => {
                if (text) {
                    saveAnnotation({
                        type: 'Text',
                        x,
                        y,
                        width: 200,
                        height: 30,
                        text,
                        fontFamily: 'Arial',
                        fontSize,
                        color: selectedColor,
                        data: { bold: false, italic: false, underline: false }
                    });
                }
                setShowTextInputModal(false);
            });
            setShowTextInputModal(true);
        } else if (selectedTool === 'signature' && userSignature) {
            const shapeSize = defaultShapeSizes.signature;
            saveAnnotation({
                type: 'Signature',
                x: x - shapeSize.width / 2,
                y: y - shapeSize.height / 2,
                width: shapeSize.width,
                height: shapeSize.height,
                data: { signatureImage: userSignature },
                color: selectedColor
            });
        } else if (['rectangle', 'circle', 'check', 'cross', 'caret'].includes(selectedTool)) {
            // Click-to-place behavior with default sizes
            const shapeSize = defaultShapeSizes[selectedTool];
            let type = selectedTool;
            if (type === 'check') type = 'Checkmark';
            if (type === 'cross') type = 'Cross';
            if (type === 'rectangle') type = 'Rectangle';
            if (type === 'circle') type = 'Circle';
            if (type === 'caret') type = 'Caret';

            saveAnnotation({
                type,
                x: x - shapeSize.width / 2,
                y: y - shapeSize.height / 2,
                width: shapeSize.width,
                height: shapeSize.height,
                color: selectedColor
            });
        }
    };

    const handleMouseMove = (e) => {
        const pos = getMousePosition(e);
        if (!pos) return;
        const { x, y } = pos;

        // Handle resize
        if (isResizing && resizeStart && selectedAnnotation) {
            handleResizeMove(x, y);
            return;
        }

        // Handle dragging annotation
        if (isDragging && draggedAnnotation && dragStart) {
            const deltaX = x - dragStart.x;
            const deltaY = y - dragStart.y;
            
            setDraggedAnnotation(prev => ({
                ...prev,
                positionX: prev.originalX + deltaX,
                positionY: prev.originalY + deltaY
            }));
            return;
        }

        if (!isDrawing) return;

        setDrawingPath(path => [...path, { x, y }]);
    };

    const handleMouseUp = async () => {
        // Handle resize end
        if (isResizing && selectedAnnotation) {
            await finishResize();
            return;
        }

        // Handle completing annotation drag
        if (isDragging && draggedAnnotation) {
            // Save the new position to the backend
            await updateAnnotationPosition(draggedAnnotation);
            setIsDragging(false);
            setDragStart(null);
            setDraggedAnnotation(null);
            return;
        }

        if (isDrawing && drawingPath.length > 1) {
            // Calculate bounding box
            const minX = Math.min(...drawingPath.map(p => p.x));
            const minY = Math.min(...drawingPath.map(p => p.y));
            const maxX = Math.max(...drawingPath.map(p => p.x));
            const maxY = Math.max(...drawingPath.map(p => p.y));
            
            if (selectedTool === 'highlight') {
                // Highlight is a semi-transparent rectangle
                saveAnnotation({
                    type: 'Highlight',
                    x: minX,
                    y: minY,
                    width: maxX - minX + 4,
                    height: maxY - minY + 4,
                    color: selectedColor,
                    data: { opacity: 0.3 }
                });
            } else {
                // Normalize path coordinates to be relative to the bounding box (0,0 origin)
                // This is crucial because the SVG will be rendered inside a container positioned at (minX, minY)
                const normalizedPath = drawingPath.map((point, index) => 
                    `${index === 0 ? 'M' : 'L'} ${point.x - minX} ${point.y - minY}`
                ).join(' ');

                saveAnnotation({
                    type: 'Drawing',
                    x: minX,
                    y: minY,
                    width: maxX - minX + 4, // Add small padding for stroke width
                    height: maxY - minY + 4,
                    data: { path: normalizedPath },
                    color: selectedColor
                });
            }
            setIsDrawing(false);
            setDrawingPath([]);
        } else if (isDrawing) {
            // Single point or no path - just cancel
            setIsDrawing(false);
            setDrawingPath([]);
        }
    };

    /**
     * Handle resize start from resize handle
     */
    const handleResizeStart = (e, handle) => {
        e.stopPropagation();
        e.preventDefault();
        if (!selectedAnnotation) return;
        
        setIsResizing(true);
        setResizeHandle(handle);
        setResizeStart({
            x: selectedAnnotation.positionX,
            y: selectedAnnotation.positionY,
            width: selectedAnnotation.width,
            height: selectedAnnotation.height,
            mouseX: e.clientX,
            mouseY: e.clientY
        });
    };

    /**
     * Handle resize move
     */
    const handleResizeMove = (x, y) => {
        if (!resizeStart || !selectedAnnotation) return;
        
        let newWidth = resizeStart.width;
        let newHeight = resizeStart.height;
        let newX = resizeStart.x;
        let newY = resizeStart.y;
        
        // Calculate new dimensions based on which handle is being dragged
        if (resizeHandle.includes('e')) {
            newWidth = Math.max(20, x - resizeStart.x);
        }
        if (resizeHandle.includes('w')) {
            const widthDelta = resizeStart.x - x + resizeStart.width;
            newWidth = Math.max(20, widthDelta);
            newX = x;
        }
        if (resizeHandle.includes('s')) {
            newHeight = Math.max(20, y - resizeStart.y);
        }
        if (resizeHandle.includes('n')) {
            const heightDelta = resizeStart.y - y + resizeStart.height;
            newHeight = Math.max(20, heightDelta);
            newY = y;
        }
        
        setSelectedAnnotation(prev => ({
            ...prev,
            positionX: newX,
            positionY: newY,
            width: newWidth,
            height: newHeight
        }));
    };

    /**
     * Finish resize and save to backend
     */
    const finishResize = async () => {
        if (!selectedAnnotation) return;
        
        try {
            setIsSaving(true);
            const response = await apiFetch(`/api/PdfAnnotation/annotations/${selectedAnnotation.annotationId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    positionX: selectedAnnotation.positionX,
                    positionY: selectedAnnotation.positionY,
                    width: selectedAnnotation.width,
                    height: selectedAnnotation.height
                })
            });

            if (response.ok) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
            } else {
                toast.error('Failed to resize annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error resizing annotation:', error);
            }
            toast.error('Failed to resize annotation');
        } finally {
            setIsSaving(false);
            setIsResizing(false);
            setResizeHandle(null);
            setResizeStart(null);
        }
    };

    /**
     * Update annotation formatting (color, text styles, etc.)
     */
    const updateAnnotationFormatting = async (updates) => {
        if (!selectedAnnotation) return;

        try {
            setIsSaving(true);
            
            // Parse existing annotation data
            let existingData = {};
            if (selectedAnnotation.annotationData) {
                try {
                    existingData = typeof selectedAnnotation.annotationData === 'string'
                        ? JSON.parse(selectedAnnotation.annotationData)
                        : selectedAnnotation.annotationData;
                } catch (catchError) {
                    Sentry.captureException(catchError);
                    existingData = {};
                }
            }
            
            // Merge new data with existing (only if updates.data is provided)
            const mergedData = updates.data ? { ...existingData, ...updates.data } : existingData;
            
            const response = await apiFetch(`/api/PdfAnnotation/annotations/${selectedAnnotation.annotationId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    positionX: selectedAnnotation.positionX,
                    positionY: selectedAnnotation.positionY,
                    width: selectedAnnotation.width,
                    height: selectedAnnotation.height,
                    color: updates.color ?? selectedAnnotation.color,
                    fontSize: updates.fontSize ?? selectedAnnotation.fontSize,
                    annotationData: JSON.stringify(mergedData)
                })
            });

            if (response.ok) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
                // Update local selected annotation state
                setSelectedAnnotation(prev => prev ? {
                    ...prev,
                    color: updates.color ?? prev.color,
                    fontSize: updates.fontSize ?? prev.fontSize,
                    annotationData: JSON.stringify(mergedData)
                } : null);
                toast.success('Annotation updated');
            } else {
                toast.error('Failed to update annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error updating annotation:', error);
            }
            toast.error('Failed to update annotation');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle click on audit trail entry - navigate to the annotation's page and highlight it (view only)
     */
    const handleAuditEntryClick = async (entry) => {
        if (!entry.annotationId || !entry.pageNumber) return;
        
        // Clear any existing selection - we only want to highlight, not select for editing
        setSelectedAnnotation(null);
        
        // Navigate to the page where the annotation is
        if (entry.pageNumber !== currentPage) {
            setCurrentPage(entry.pageNumber);
            // Wait for page to render
            await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Find the annotation and highlight it (view only, not editable)
        const annotation = annotations.find(a => a.annotationId === entry.annotationId);
        if (annotation) {
            // Set temporary highlight (will auto-clear after 3 seconds)
            setHighlightedAnnotationId(entry.annotationId);
            setTimeout(() => setHighlightedAnnotationId(null), 3000);
            
            toast.info(`Viewing annotation on page ${entry.pageNumber}`, {
                autoClose: 2000
            });
        } else {
            // Annotation might not be in current version
            toast.warning('Annotation not found in current version. Try selecting the version from history.', {
                autoClose: 4000
            });
        }
    };

    /**
     * Get parsed annotation data
     */
    const getAnnotationData = (annotation) => {
        if (!annotation?.annotationData) return {};
        try {
            return typeof annotation.annotationData === 'string'
                ? JSON.parse(annotation.annotationData)
                : annotation.annotationData;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return {};
        }
    };

    /**
     * Render cursor preview for shape tools
     */
    const renderCursorPreview = () => {
        if (!mousePosition) return null;
        if (selectedTool === 'select' || selectedTool === 'draw' || selectedTool === 'text' || 
            selectedTool === 'highlight') return null;
        
        // Special handling for signature - only show if user has a signature
        if (selectedTool === 'signature' && !userSignature) return null;
        
        const shapeSize = defaultShapeSizes[selectedTool];
        if (!shapeSize) return null;
        
        const previewStyle = {
            position: 'absolute',
            left: `${(mousePosition.x - shapeSize.width / 2) * scale}px`,
            top: `${(mousePosition.y - shapeSize.height / 2) * scale}px`,
            width: `${shapeSize.width * scale}px`,
            height: `${shapeSize.height * scale}px`,
            pointerEvents: 'none',
            opacity: 0.6,
            zIndex: 9999
        };

        switch (selectedTool) {
            case 'rectangle':
                return (
                    <div style={{ ...previewStyle, border: `2px dashed ${selectedColor}`, borderRadius: '2px' }} />
                );
            case 'circle':
                return (
                    <div style={{ ...previewStyle, border: `2px dashed ${selectedColor}`, borderRadius: '50%' }} />
                );
            case 'check':
                return (
                    <div style={{ ...previewStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaCheck size={shapeSize.height * scale * 0.8} color={selectedColor} style={{ opacity: 0.7 }} />
                    </div>
                );
            case 'cross':
                return (
                    <div style={{ ...previewStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaCross size={shapeSize.height * scale * 0.8} color={selectedColor} style={{ opacity: 0.7 }} />
                    </div>
                );
            case 'caret':
                return (
                    <div style={{ ...previewStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaCaretUp size={shapeSize.height * scale * 0.8} color={selectedColor} style={{ opacity: 0.7 }} />
                    </div>
                );
            case 'signature':
                return (
                    <div style={{ 
                        ...previewStyle, 
                        border: '2px dashed #1976d2', 
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        {userSignature && (
                            <img 
                                src={userSignature} 
                                alt="Signature preview" 
                                style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: '100%', 
                                    objectFit: 'contain',
                                    opacity: 0.7
                                }} 
                            />
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    /**
     * Render inline edit menu for selected annotation
     */
    const renderEditMenu = () => {
        if (!selectedAnnotation || selectedTool !== 'select') return null;
        
        const annData = getAnnotationData(selectedAnnotation);
        const isText = selectedAnnotation.annotationType === 'Text';
        
        return (
            <div 
                className="annotation-edit-menu"
                style={{
                    left: `${(selectedAnnotation.positionX + selectedAnnotation.width / 2) * scale}px`,
                    top: `${(selectedAnnotation.positionY - 10) * scale}px`
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="edit-menu-content">
                    {/* Color picker */}
                    <div className="edit-menu-item">
                        <label title="Change color">
                            <FaPalette size={12} />
                            <input 
                                type="color" 
                                value={selectedAnnotation.color || '#000000'}
                                onChange={(e) => updateAnnotationFormatting({ color: e.target.value })}
                            />
                        </label>
                    </div>
                    
                    {/* Text-specific controls */}
                    {isText && (
                        <>
                            <div className="edit-menu-divider" />
                            <button
                                className={`edit-menu-btn ${annData.bold ? 'active' : ''}`}
                                onClick={() => updateAnnotationFormatting({ data: { bold: !annData.bold } })}
                                title="Bold"
                            >
                                <FaBold size={12} />
                            </button>
                            <button
                                className={`edit-menu-btn ${annData.italic ? 'active' : ''}`}
                                onClick={() => updateAnnotationFormatting({ data: { italic: !annData.italic } })}
                                title="Italic"
                            >
                                <FaItalic size={12} />
                            </button>
                            <button
                                className={`edit-menu-btn ${annData.underline ? 'active' : ''}`}
                                onClick={() => updateAnnotationFormatting({ data: { underline: !annData.underline } })}
                                title="Underline"
                            >
                                <FaUnderline size={12} />
                            </button>
                            <div className="edit-menu-divider" />
                            <button
                                className="edit-menu-btn"
                                onClick={() => updateAnnotationFormatting({ fontSize: Math.max(8, (selectedAnnotation.fontSize || 14) - 2) })}
                                title="Decrease font size"
                            >
                                <FaMinus size={10} />
                            </button>
                            <span className="font-size-display">{selectedAnnotation.fontSize || 14}</span>
                            <button
                                className="edit-menu-btn"
                                onClick={() => updateAnnotationFormatting({ fontSize: Math.min(72, (selectedAnnotation.fontSize || 14) + 2) })}
                                title="Increase font size"
                            >
                                <FaPlus size={10} />
                            </button>
                        </>
                    )}
                    
                    <div className="edit-menu-divider" />
                    <button 
                        className="edit-menu-btn delete"
                        onClick={handleDeleteAnnotation}
                        title="Delete annotation"
                    >
                        <FaTrash size={12} />
                    </button>
                </div>
            </div>
        );
    };

    /**
     * Render resize handles for selected annotation
     */
    const renderResizeHandles = () => {
        if (!selectedAnnotation || selectedTool !== 'select') return null;
        
        const handles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
        
        return handles.map(handle => {
            let style = { position: 'absolute' };
            
            switch (handle) {
                case 'nw': style = { ...style, top: '-4px', left: '-4px', cursor: 'nw-resize' }; break;
                case 'n': style = { ...style, top: '-4px', left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize' }; break;
                case 'ne': style = { ...style, top: '-4px', right: '-4px', cursor: 'ne-resize' }; break;
                case 'e': style = { ...style, top: '50%', right: '-4px', transform: 'translateY(-50%)', cursor: 'e-resize' }; break;
                case 'se': style = { ...style, bottom: '-4px', right: '-4px', cursor: 'se-resize' }; break;
                case 's': style = { ...style, bottom: '-4px', left: '50%', transform: 'translateX(-50%)', cursor: 's-resize' }; break;
                case 'sw': style = { ...style, bottom: '-4px', left: '-4px', cursor: 'sw-resize' }; break;
                case 'w': style = { ...style, top: '50%', left: '-4px', transform: 'translateY(-50%)', cursor: 'w-resize' }; break;
                default: break;
            }
            
            return (
                        <div
                            key={handle}
                            className="resize-handle"
                            style={style}
                            onMouseDown={(e) => handleResizeStart(e, handle)}
                        />
                    );
                });
            };

            /**
             * Get filename without extension
             */
            const getFileNameWithoutExtension = (fileName) => {
                const lastDotIndex = fileName.lastIndexOf('.');
                if (lastDotIndex === -1) return fileName;
                return fileName.substring(0, lastDotIndex);
            };

            /**
             * Get file extension including the dot
             */
            const getFileExtension = (fileName) => {
                const lastDotIndex = fileName.lastIndexOf('.');
                if (lastDotIndex === -1) return '';
                return fileName.substring(lastDotIndex);
            };

            /**
             * Start renaming the file
             */
            const startRename = () => {
                const nameWithoutExt = getFileNameWithoutExtension(currentFileName);
                setRenameValue(nameWithoutExt);
                setIsRenaming(true);
                // Focus the input after it renders
                setTimeout(() => {
                    if (renameInputRef.current) {
                        renameInputRef.current.focus();
                        renameInputRef.current.select();
                    }
                }, 0);
            };

            /**
             * Cancel renaming
             */
            const cancelRename = () => {
                setIsRenaming(false);
                setRenameValue('');
            };

            /**
             * Save the renamed file
             */
            const saveRename = async () => {
                if (!renameValue.trim()) {
                    toast.error('Filename cannot be empty');
                    return;
                }

                const extension = getFileExtension(currentFileName);
                const newFileName = renameValue.trim() + extension;

                if (newFileName === currentFileName) {
                    setIsRenaming(false);
                            return;
                        }

                        try {
                            setIsSaving(true);
                            const response = await apiFetch(`/api/ActionItems/attachments/${attachment.attachmentId}/rename`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ newFileName })
                            });

                            if (response.ok) {
                                setCurrentFileName(newFileName);
                                toast.success('File renamed successfully');
                            } else {
                                const errorData = await response.json();
                                toast.error(errorData.message || 'Failed to rename file');
                            }
                        } catch (error) {
                            Sentry.captureException(error);
                            if (process.env.NODE_ENV === 'development') {
                                console.error('Error renaming file:', error);
                            }
                            toast.error('Failed to rename file');
                        } finally {
                            setIsSaving(false);
                            setIsRenaming(false);
                        }
                    };

            /**
             * Handle rename input key events
             */
            const handleRenameKeyDown = (e) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    saveRename();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    cancelRename();
                }
            };

            /**
             * Create new version
             */
            const createNewVersion = async () => {
                setVersionDescription('');
                setShowVersionModal(true);
            };

    /**
     * Handle version creation submission
     */
    const handleVersionCreate = async () => {
        if (!versionDescription.trim()) {
            toast.error('Please enter a version description');
            return;
        }

        try {
            const createBody = contractDocumentMode
                ? { versionDescription: versionDescription }
                : { attachmentId: attachment.attachmentId, versionDescription: versionDescription };

            const response = await apiFetch(createVersionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(createBody)
            });

            const data = await response.json();
            if (data.success) {
                setCurrentVersion(data.data);
                await loadVersions();
                await loadAuditTrail();
                setAnnotations([]);
                setShowVersionModal(false);
                setVersionDescription('');
                toast.success('New version created');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error creating version:', error);
            }
            toast.error('Failed to create version');
        }
    };

    /**
     * Handle signature tool click - prompt to create signature if none exists
     */
    const handleSignatureToolClick = useCallback(() => {
        if (userSignature) {
            setSelectedTool('signature');
        } else {
            // Open signature creation modal
            setShowSignatureModal(true);
            setSignatureDrawing([]);
        }
    }, [userSignature]);

    /**
     * Handle signature canvas mouse events
     */
    const handleSignatureCanvasMouseDown = (e) => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;
        
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        setIsDrawingSignature(true);
        setSignatureDrawing([{ x, y }]);
    };

    const handleSignatureCanvasMouseMove = (e) => {
        if (!isDrawingSignature) return;
        
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;
        
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        setSignatureDrawing(prev => [...prev, { x, y }]);
        
        // Draw on canvas
        const ctx = canvas.getContext('2d');
        if (signatureDrawing.length > 0) {
            const lastPoint = signatureDrawing[signatureDrawing.length - 1];
            ctx.beginPath();
            ctx.moveTo(lastPoint.x, lastPoint.y);
            ctx.lineTo(x, y);
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.stroke();
        }
    };

    const handleSignatureCanvasMouseUp = () => {
        setIsDrawingSignature(false);
    };

    /**
     * Clear signature canvas
     */
    const clearSignatureCanvas = () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureDrawing([]);
    };

    /**
     * Remove background from image and make it transparent
     */
    const removeBackground = (imageData) => {
        const data = imageData.data;
        const threshold = 240; // Pixels with RGB values above this will be made transparent
        
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            
            // Check if pixel is close to white (background)
            if (r > threshold && g > threshold && b > threshold) {
                data[i + 3] = 0; // Make transparent
            }
        }
        
        return imageData;
    };

    /**
     * Handle signature file upload
     */
    const handleSignatureFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = signatureCanvasRef.current;
                if (!canvas) return;
                
                const ctx = canvas.getContext('2d');
                
                // Clear canvas
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                
                // Calculate scaling to fit signature in canvas
                const maxWidth = canvas.width - 20;
                const maxHeight = canvas.height - 20;
                let width = img.width;
                let height = img.height;
                
                if (width > maxWidth) {
                    height = (maxWidth / width) * height;
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    width = (maxHeight / height) * width;
                    height = maxHeight;
                }
                
                // Center the image
                const x = (canvas.width - width) / 2;
                const y = (canvas.height - height) / 2;
                
                // Draw image
                ctx.drawImage(img, x, y, width, height);
                
                // Remove background
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const processedData = removeBackground(imageData);
                ctx.putImageData(processedData, 0, 0);
                
                // Mark as having content
                setSignatureDrawing([{ x: 0, y: 0 }]);
                
                toast.success('Signature uploaded and background removed');
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
        
        // Reset file input
        if (signatureFileInputRef.current) {
            signatureFileInputRef.current.value = '';
        }
    };

    /**
     * Save signature to user profile
     */
    const saveSignature = async () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;
        
        // Check if canvas has content
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((value, index) => index % 4 === 3 && value > 0);
        
        if (!hasContent) {
            toast.warning('Please draw or upload a signature first');
            return;
        }
        
        try {
            setIsSaving(true);
            
            // Get signature as PNG data URL (preserves transparency)
            const signatureData = canvas.toDataURL('image/png');
            
            // Save to user profile
            const response = await apiFetch('/api/User/signature', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ signatureData })
            });
            
            if (response.ok) {
                setUserSignature(signatureData);
                setShowSignatureModal(false);
                setSignatureDrawing([]);
                setSelectedTool('signature');
                toast.success('Signature saved! You can now add it to documents.');
            } else {
                toast.error('Failed to save signature');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error saving signature:', error);
            }
            toast.error('Failed to save signature');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Handle text selection - show floating toolbar when text is selected
     */
    const handleTextSelection = () => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed || !selection.rangeCount) {
            setTextSelection(null);
            return;
        }
        
        // Check if selection is within the PDF text layer
        const textLayer = canvasRef.current?.querySelector('.react-pdf__Page__textContent');
        if (!textLayer) {
            setTextSelection(null);
            return;
        }
        
        const range = selection.getRangeAt(0);
        
        // Verify selection is within the text layer
        if (!textLayer.contains(range.commonAncestorContainer)) {
            setTextSelection(null);
            return;
        }
        
        const rects = range.getClientRects();
        
        if (rects.length === 0) {
            setTextSelection(null);
            return;
        }
        
        // Get the PDF page wrapper for coordinate calculation
        const pdfPageWrapper = canvasRef.current?.querySelector('.pdf-page-wrapper');
        if (!pdfPageWrapper) return;
        
        const pageRect = pdfPageWrapper.getBoundingClientRect();
        
        // Calculate position for floating toolbar (above the selection)
        const firstRect = rects[0];
        const toolbarPosition = {
            x: firstRect.left - pageRect.left + (firstRect.width / 2),
            y: firstRect.top - pageRect.top - 10
        };
        
        // Store selected text, its bounding rectangles, and toolbar position
        setTextSelection({
            text: selection.toString(),
            toolbarPosition,
            rects: Array.from(rects).map(rect => ({
                x: (rect.left - pageRect.left) / scale,
                y: (rect.top - pageRect.top) / scale,
                width: rect.width / scale,
                height: rect.height / scale
            }))
        });
    };

    /**
     * Apply highlight to selected text
     */
    const applyHighlightToSelection = async () => {
        if (!textSelection || !textSelection.rects.length) return;
        
        try {
            // Create a highlight annotation for each selected text rectangle
            for (const rect of textSelection.rects) {
                await saveAnnotation({
                    type: 'Highlight',
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height,
                    color: selectedColor,
                    data: { opacity: 0.3, highlightedText: textSelection.text }
                });
            }
            
            // Clear selection
            window.getSelection()?.removeAllRanges();
            setTextSelection(null);
            toast.success('Text highlighted');
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error applying highlight:', error);
            }
            toast.error('Failed to apply highlight');
        }
    };

    /**
     * Apply redaction to selected text
     */
    const applyRedaction = async () => {
        if (!textSelection || !textSelection.rects.length) {
            toast.warning('Please select text to redact');
            return;
        }
        
        try {
            // Create a redaction annotation for each selected text rectangle
            for (const rect of textSelection.rects) {
                await saveAnnotation({
                    type: 'Redaction',
                    x: rect.x,
                    y: rect.y,
                    width: rect.width,
                    height: rect.height,
                    text: textSelection.text,
                    color: '#000000',
                    data: { redactedText: textSelection.text }
                });
            }
            
            // Clear selection
            window.getSelection()?.removeAllRanges();
            setTextSelection(null);
            toast.success('Text redacted');
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error applying redaction:', error);
            }
            toast.error('Failed to apply redaction');
        }
    };

    /**
     * Handle delete annotation
     */
    const handleDeleteAnnotation = useCallback(async () => {
        if (!selectedAnnotation) return;

        try {
            setIsSaving(true);
            const response = await apiFetch(`/api/PdfAnnotation/annotations/${selectedAnnotation.annotationId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
                setSelectedAnnotation(null);
                toast.success('Annotation deleted');
            } else {
                toast.error('Failed to delete annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error deleting annotation:', error);
            }
            toast.error('Failed to delete annotation');
        } finally {
            setIsSaving(false);
        }
    }, [selectedAnnotation, apiFetch, currentVersion, loadAnnotations, loadAuditTrail]);

    /**
     * Handle undo last annotation on current page
     */
    const handleUndoLastAnnotation = useCallback(async () => {
        const pageAnnotations = annotations.filter(a => a.pageNumber === currentPage);
        if (pageAnnotations.length === 0) return;

        const lastAnnotation = pageAnnotations[pageAnnotations.length - 1];
        
        try {
            setIsSaving(true);
            const response = await apiFetch(`/api/PdfAnnotation/annotations/${lastAnnotation.annotationId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                await loadAnnotations(currentVersion.pdfVersionId);
                await loadAuditTrail();
                toast.success('Annotation removed');
            } else {
                toast.error('Failed to undo annotation');
            }
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error undoing annotation:', error);
            }
            toast.error('Failed to undo annotation');
        } finally {
            setIsSaving(false);
        }
    }, [annotations, currentPage, apiFetch, currentVersion, loadAnnotations, loadAuditTrail]);

    /**
     * Handle annotation click for selection
     */
    const handleAnnotationClick = (e, annotation) => {
        e.stopPropagation();
        if (selectedTool === 'select') {
            setSelectedAnnotation(selectedAnnotation?.annotationId === annotation.annotationId ? null : annotation);
        }
    };

    /**
     * Switch to a different version
     */
    const handleVersionSwitch = async (version) => {
        try {
            setLoading(true);
            setCurrentVersion(version);
            await loadAnnotations(version.pdfVersionId);
            setShowVersionSelector(false);
            setSelectedAnnotation(null);
            toast.success(`Switched to version: ${version.versionDescription}`);
        } catch (error) {
            Sentry.captureException(error);
            if (process.env.NODE_ENV === 'development') {
                console.error('Error switching version:', error);
            }
            toast.error('Failed to switch version');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle keyboard shortcuts
     */
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            
            // Don't handle shortcuts when typing in inputs
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            switch (e.key.toLowerCase()) {
                case 'escape':
                    if (showSignatureModal) {
                        setShowSignatureModal(false);
                    } else if (showTextInputModal) {
                        setShowTextInputModal(false);
                    } else if (showVersionModal) {
                        setShowVersionModal(false);
                    } else if (selectedAnnotation) {
                        setSelectedAnnotation(null);
                    } else {
                       handleClose();
                    }
                    break;
                case 'v':
                    setSelectedTool('select');
                    break;
                case 't':
                    setSelectedTool('text');
                    break;
                case 'd':
                    setSelectedTool('draw');
                    break;
                case 's':
                    if (!e.ctrlKey) handleSignatureToolClick();
                    break;
                case 'r':
                    setSelectedTool('rectangle');
                    break;
                case 'c':
                    if (!e.ctrlKey) setSelectedTool('circle');
                    break;
                case 'k':
                    setSelectedTool('check');
                    break;
                case 'x':
                    setSelectedTool('cross');
                    break;
                case 'a':
                    setSelectedTool('caret');
                    break;
                case 'h':
                    setSelectedTool('highlight');
                    break;
                case 'delete':
                case 'backspace':
                    if (selectedAnnotation) {
                        e.preventDefault();
                        handleDeleteAnnotation();
                    }
                    break;
                case 'z':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        handleUndoLastAnnotation();
                    }
                    break;
                case 'arrowleft':
                    if (currentPage > 1) goToPrevPage();
                    break;
                case 'arrowright':
                    if (currentPage < numPages) goToNextPage();
                    break;
                case '+':
                case '=':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        zoomIn();
                    }
                    break;
                case '-':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        zoomOut();
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, showTextInputModal, showVersionModal, showSignatureModal, selectedAnnotation, currentPage, numPages, goToNextPage, handleDeleteAnnotation, handleSignatureToolClick, handleUndoLastAnnotation, handleClose]);

    /**
     * Reset state when modal closes
     */
    useEffect(() => {
        (async () => {
            if (!isOpen) {
                setCurrentPage(1);
                setScale(1.0);
                setPageDimensions({ width: 0, height: 0 });
                setSelectedTool('select');
                setSelectedAnnotation(null);
                setIsDrawing(false);
                setDrawingPath([]);
                setShowAuditTrail(false);
                setShowVersionSelector(false);
                setIsDragging(false);
                setDragStart(null);
                setDraggedAnnotation(null);
                setMousePosition(null);
                setIsResizing(false);
                setResizeHandle(null);
                setResizeStart(null);
                setShowSignatureModal(false);
                setSignatureDrawing([]);
                setIsDrawingSignature(false);
                setHighlightedAnnotationId(null);
                setIsRenaming(false);
                setRenameValue('');
                setCurrentFileName('');
            }
        })();
    }, [isOpen]);

            if (!isOpen) return null;

            // Show loading state while checking authentication
            if (authLoading) {
                return ReactDOM.createPortal(
                    <div className="pdf-annotation-modal">
                        <div className="modal-backdrop" onClick={handleClose}></div>
                        <div className="modal-content">
                            <div className="loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <div className="loading-spinner"></div>
                                <span>Checking authentication...</span>
                            </div>
                        </div>
                    </div>,
                    document.body
                );
            }

            // Redirect unauthenticated users
            if (!isAuthenticated) {
                return ReactDOM.createPortal(
                    <div className="pdf-annotation-modal">
                        <div className="modal-backdrop" onClick={handleClose}></div>
                        <div className="modal-content">
                            <div className="modal-header">
                                <div className="header-title">
                                    <h2>Authentication Required</h2>
                                </div>
                                <div className="header-actions">
                                    <button onClick={handleClose} className="close-btn" title="Close">
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
                                <div style={{ fontSize: '3rem', color: '#f44336' }}>
                                    <FaTimes />
                                </div>
                                <h3 style={{ margin: 0, color: '#333' }}>Access Denied</h3>
                                <p style={{ margin: 0, color: '#666', textAlign: 'center' }}>
                                    You must be logged in to view and annotate PDF documents.
                                </p>
                                <button 
                                    onClick={handleClose}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: '#1976d2',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '500'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                );
            }

            // Use portal to render modal at document body level to escape any parent CSS constraints
            const modalContent = (
                <div className={`pdf-annotation-modal ${isSaving ? 'saving' : ''}`}>
                    <div className="modal-backdrop" onClick={handleClose}></div>
                    <div className="modal-content">
                        {/* Header */}
                        <div className="modal-header">
                            <div className="header-title">
                                {isRenaming ? (
                                    <div className="filename-rename">
                                        <input
                                            ref={renameInputRef}
                                            type="text"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onKeyDown={handleRenameKeyDown}
                                    onBlur={saveRename}
                                    className="rename-input"
                                />
                                <span className="file-extension">{getFileExtension(currentFileName)}</span>
                            </div>
                        ) : (
                            <h2 
                                onDoubleClick={startRename}
                                className="filename-editable"
                                title="Double-click to rename"
                            >
                                {currentFileName || attachment.fileName}
                            </h2>
                        )}
                        {currentVersion && (
                            <div className="version-selector">
                                <button 
                                    onClick={() => setShowVersionSelector(!showVersionSelector)}
                                    className="version-btn"
                                    title="Switch version"
                                >
                                    <FaLayerGroup size={14} />
                                    <span>{currentVersion.versionDescription || `v${currentVersion.versionNumber}`}</span>
                                </button>
                                {showVersionSelector && versions.length > 0 && (
                                    <div className="version-dropdown">
                                        {versions.map(version => (
                                            <button
                                                key={version.pdfVersionId}
                                                onClick={() => handleVersionSwitch(version)}
                                                className={version.pdfVersionId === currentVersion.pdfVersionId ? 'active' : ''}
                                            >
                                                <span className="version-name">
                                                    {version.versionDescription || `Version ${version.versionNumber}`}
                                                </span>
                                                <span className="version-date">
                                                    {new Date(version.createdAt).toLocaleDateString()}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="header-actions">
                        <button 
                            onClick={() => setShowAuditTrail(!showAuditTrail)} 
                            className={showAuditTrail ? 'active' : ''}
                            title="View history"
                        >
                            <FaHistory size={16} /> History
                        </button>
                        <button onClick={createNewVersion} title="Create new version">
                            <FaSave size={16} /> New Version
                        </button>
                        <button onClick={handleClose} className="close-btn" title="Close (Esc)">
                            <FaTimes size={16} />
                        </button>
                    </div>
                </div>

                {/* Toolbar */}
                <div className="annotation-toolbar">
                    <div className="tool-group">
                        <label>Tools:</label>
                        <button
                            className={selectedTool === 'select' ? 'active' : ''}
                            onClick={() => setSelectedTool('select')}
                            title="Select/Move (V)"
                        >
                            <FaMousePointer size={16} />
                        </button>
                        <button
                            className={selectedTool === 'text' ? 'active' : ''}
                            onClick={() => setSelectedTool(selectedTool === 'text' ? 'select' : 'text')}
                            title="Add text (T)"
                        >
                            <FaFont size={16} />
                        </button>
                        <button
                            className={selectedTool === 'draw' ? 'active' : ''}
                            onClick={() => setSelectedTool(selectedTool === 'draw' ? 'select' : 'draw')}
                            title="Draw (D)"
                        >
                            <FaPen size={16} />
                        </button>
                        <button
                            className={selectedTool === 'signature' ? 'active' : ''}
                            onClick={() => selectedTool === 'signature' ? setSelectedTool('select') : handleSignatureToolClick()}
                            title={userSignature ? "Add signature (S)" : "Set up signature first"}
                        >
                            <FaSignature size={16} />
                        </button>
                        <button
                            className={selectedTool === 'rectangle' ? 'active' : ''}
                            onClick={() => setSelectedTool(selectedTool === 'rectangle' ? 'select' : 'rectangle')}
                            title="Rectangle (R)"
                        >
                            <FaSquare size={16} />
                        </button>
                        <button
                            className={selectedTool === 'circle' ? 'active' : ''}
                            onClick={() => setSelectedTool(selectedTool === 'circle' ? 'select' : 'circle')}
                            title="Circle (C)"
                        >
                            <FaCircle size={16} />
                        </button>
                        <button
                            className={selectedTool === 'check' ? 'active' : ''}
                            onClick={() => setSelectedTool(selectedTool === 'check' ? 'select' : 'check')}
                            title="Checkmark (K)"
                        >
                            <FaCheck size={16} />
                        </button>
                        <button
                                className={selectedTool === 'cross' ? 'active' : ''}
                                onClick={() => setSelectedTool(selectedTool === 'cross' ? 'select' : 'cross')}
                                title="Cross (X)"
                            >
                                <FaCross size={16} />
                            </button>
                            <button
                                className={selectedTool === 'caret' ? 'active' : ''}
                                onClick={() => setSelectedTool(selectedTool === 'caret' ? 'select' : 'caret')}
                                title="Caret (A)"
                            >
                                <FaCaretUp size={16} />
                            </button>
                            <button
                                className={selectedTool === 'highlight' ? 'active' : ''}
                                onClick={() => setSelectedTool(selectedTool === 'highlight' ? 'select' : 'highlight')}
                                title="Highlight (H) - or select text on PDF"
                            >
                                <FaHighlighter size={16} />
                            </button>
                        </div>
                    <div className="tool-group">
                        <label>Color:</label>
                        <input
                            type="color"
                            value={selectedColor}
                            onChange={(e) => handleColorChange(e.target.value)}
                            title="Color"
                        />
                        <label>Size:</label>
                        <input
                            type="number"
                            value={fontSize}
                            onChange={(e) => setFontSize(Number(e.target.value))}
                            min="8"
                            max="72"
                            title="Font size"
                        />
                    </div>
                    <div className="tool-group nav-controls">
                        <button onClick={goToPrevPage} disabled={currentPage <= 1} title="Previous page (?)">
                            <FaChevronLeft size={12} />
                        </button>
                        <span className="page-info">{currentPage}/{numPages}</span>
                        <button onClick={goToNextPage} disabled={currentPage >= numPages} title="Next page (?)">
                            <FaChevronRight size={12} />
                        </button>
                        <button onClick={zoomOut} disabled={scale <= 0.5} title="Zoom out (Ctrl+-)">
                            <FaSearchMinus size={12} />
                        </button>
                        <span className="zoom-info">{Math.round(scale * 100)}%</span>
                        <button onClick={zoomIn} disabled={scale >= 3.0} title="Zoom in (Ctrl++)">
                            <FaSearchPlus size={12} />
                        </button>
                    </div>
                    <div className="tool-group actions">
                        {selectedAnnotation && (
                            <button
                                onClick={handleDeleteAnnotation}
                                className="delete-btn"
                                title="Delete selected annotation (Del)"
                            >
                                <FaTrash size={14} />
                            </button>
                        )}
                        <button
                            onClick={handleUndoLastAnnotation}
                            disabled={annotations.filter(a => a.pageNumber === currentPage).length === 0}
                            title="Undo last annotation (Ctrl+Z)"
                        >
                            <FaUndo size={14} />
                        </button>
                    </div>
                </div>


                {/* Content area */}
                <div className="modal-body">
                    {/* Main PDF viewer */}
                    <div className="pdf-viewer-area">
                        <div 
                            className={`pdf-canvas-container ${selectedTool !== 'select' ? 'tool-active' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
                            ref={canvasRef}
                            onMouseUp={handleTextSelection}
                            onMouseLeave={() => {
                                if (isDrawing) {
                                    setIsDrawing(false);
                                    setDrawingPath([]);
                                }
                                if (isDragging) {
                                    setIsDragging(false);
                                    setDragStart(null);
                                    setDraggedAnnotation(null);
                                }
                                if (isResizing) {
                                    setIsResizing(false);
                                    setResizeHandle(null);
                                    setResizeStart(null);
                                }
                            }}
                        >
                            {loading ? (
                                <div className="loading">
                                    <div className="loading-spinner"></div>
                                    <span>Loading PDF...</span>
                                </div>
                            ) : (
                                <div 
                                    className="pdf-page-wrapper"
                                    ref={pdfPageWrapperRef}
                                    onMouseMove={handleWrapperMouseMove}
                                    onMouseLeave={() => setMousePosition(null)}
                                >
                                    {/* Show load error (auth fetch OR PDF.js render failure)
                                        with a retry affordance so users can recover from a
                                        transient problem without reopening the modal. */}
                                    {pdfLoadError ? (
                                        <div className="error-message pdf-load-error">
                                            <span>{pdfLoadError}</span>
                                            <button
                                                type="button"
                                                className="pdf-retry-btn"
                                                onClick={handleRetryPdf}
                                            >
                                                <FaUndo size={12} /> Try again
                                            </button>
                                        </div>
                                    ) : (
                                        // Use the authenticated Blob URL so react-pdf can load the file
                                        // without needing to attach JWT headers to its own request.
                                        <Document
                                            key={pdfReloadKey}
                                            file={pdfBlobUrl}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                            onLoadError={onDocumentLoadError}
                                            onSourceError={onDocumentLoadError}
                                            loading={<div className="loading"><div className="loading-spinner"></div><span>Loading PDF...</span></div>}
                                            error={<div className="error-message">Failed to load PDF. Please try again.</div>}
                                        >
                                        <Page 
                                                pageNumber={currentPage} 
                                                scale={scale}
                                                renderTextLayer={true}
                                                renderAnnotationLayer={false}
                                                onRenderSuccess={onPageRenderSuccess}
                                            />
                                        </Document>
                                    )}

                                    {/* Text Selection Floating Toolbar */}
                                    {textSelection && textSelection.toolbarPosition && (
                                        <div 
                                            className="text-selection-toolbar"
                                            style={{
                                                left: `${textSelection.toolbarPosition.x}px`,
                                                top: `${textSelection.toolbarPosition.y}px`
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <button
                                                onClick={applyHighlightToSelection}
                                                title="Highlight selected text"
                                                className="highlight-btn"
                                            >
                                                <FaHighlighter size={14} />
                                                <span>Highlight</span>
                                            </button>
                                            <button
                                                onClick={applyRedaction}
                                                title="Redact selected text"
                                                className="redact-btn"
                                            >
                                                <FaEraser size={14} />
                                                <span>Redact</span>
                                            </button>
                                        </div>
                                    )}

                                    {/* Annotation Layer - Positioned over the PDF with exact dimensions */}
                                    <div 
                                        className="annotation-layer" 
                                        ref={annotationLayerRef}
                                        onMouseDown={(e) => {
                                            // Clear text selection when clicking on annotation layer
                                            if (textSelection) {
                                                window.getSelection()?.removeAllRanges();
                                                setTextSelection(null);
                                            }
                                            handleMouseDown(e);
                                        }}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            setSelectedTool('select');
                                        }}
                                        style={{
                                            width: pageDimensions.width > 0 ? `${pageDimensions.width}px` : '100%',
                                            height: pageDimensions.height > 0 ? `${pageDimensions.height}px` : '100%'
                                        }}
                                        onClick={(e) => {
                                            // Only deselect if clicking directly on the layer, not on an annotation
                                            if (e.target === annotationLayerRef.current) {
                                                setSelectedAnnotation(null);
                                            }
                                        }}
                                    >
                                        {/* Render annotations for current page - show dragged/resized position */}
                                        {annotations
                                            .filter(a => a.pageNumber === currentPage)
                                            .map(annotation => {
                                                // Use dragged position if this is the annotation being dragged
                                                // Use selectedAnnotation dimensions if this annotation is being resized
                                                let displayAnnotation = annotation;
                                                if (isDragging && draggedAnnotation?.annotationId === annotation.annotationId) {
                                                    displayAnnotation = draggedAnnotation;
                                                } else if (isResizing && selectedAnnotation?.annotationId === annotation.annotationId) {
                                                    displayAnnotation = selectedAnnotation;
                                                }
                                                
                                                const isSelected = selectedAnnotation?.annotationId === annotation.annotationId;
                                                const isHighlighted = highlightedAnnotationId === annotation.annotationId;
                                                
                                                return (
                                                <div
                                                    key={annotation.annotationId}
                                                    className={`annotation-item ${annotation.annotationType.toLowerCase()} ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''} ${isDragging && draggedAnnotation?.annotationId === annotation.annotationId ? 'dragging' : ''} ${isResizing && isSelected ? 'resizing' : ''}`}
                                                    onClick={(e) => handleAnnotationClick(e, annotation)}
                                                    style={{
                                                        left: `${displayAnnotation.positionX * scale}px`,
                                                        top: `${displayAnnotation.positionY * scale}px`,
                                                        width: `${displayAnnotation.width * scale}px`,
                                                        height: `${displayAnnotation.height * scale}px`,
                                                        border: (annotation.annotationType === 'Rectangle' || annotation.annotationType === 'Circle') ? `2px solid ${annotation.color}` : 'none',
                                                        borderRadius: annotation.annotationType === 'Circle' ? '50%' : '0',
                                                        color: annotation.color,
                                                        fontSize: annotation.fontSize ? `${annotation.fontSize * scale}px` : 'inherit',
                                                        zIndex: annotation.zIndex || 1,
                                                        cursor: selectedTool === 'select' ? (isSelected ? 'move' : 'pointer') : 'inherit'
                                                    }}
                                                >
                                                    {annotation.annotationType === 'Text' && (() => {
                                                        const data = getAnnotationData(annotation);
                                                        return (
                                                            <span style={{ 
                                                                fontFamily: annotation.fontFamily || 'Arial',
                                                                fontWeight: data.bold ? 'bold' : 'normal',
                                                                fontStyle: data.italic ? 'italic' : 'normal',
                                                                textDecoration: data.underline ? 'underline' : 'none'
                                                            }}>
                                                                {annotation.textContent}
                                                            </span>
                                                        );
                                                    })()}
                                                    {annotation.annotationType === 'Signature' && annotation.annotationData && (() => {
                                                        try {
                                                            const data = typeof annotation.annotationData === 'string' 
                                                                ? JSON.parse(annotation.annotationData) 
                                                                : annotation.annotationData;
                                                            if (!data?.signatureImage) return null;
                                                            return (
                                                                <img 
                                                                    src={data.signatureImage} 
                                                                    alt="Signature"
                                                                />
                                                            );
                                                        } catch (catchError) {
                                                            Sentry.captureException(catchError);
                                                            return null;
                                                        }
                                                    })()}
                                                    {annotation.annotationType === 'Checkmark' && (
                                                        <FaCheck size={annotation.height * scale * 0.8} color={annotation.color} />
                                                    )}
                                                    {annotation.annotationType === 'Cross' && (
                                                        <FaCross size={annotation.height * scale * 0.8} color={annotation.color} />
                                                    )}
                                                    {annotation.annotationType === 'Drawing' && annotation.annotationData && (() => {
                                                                    try {
                                                                        const data = typeof annotation.annotationData === 'string' 
                                                                            ? JSON.parse(annotation.annotationData) 
                                                                            : annotation.annotationData;
                                                                        const pathData = data?.path;
                                                                        if (!pathData || typeof pathData !== 'string') return null;
                                                            
                                                                        // The path is stored with normalized coordinates (0,0 origin)
                                                                        // We need to render it using a viewBox that matches the original dimensions
                                                                        return (
                                                                            <svg 
                                                                                width="100%" 
                                                                                height="100%" 
                                                                                viewBox={`0 0 ${annotation.width} ${annotation.height}`}
                                                                                preserveAspectRatio="none"
                                                                                style={{ overflow: 'visible', position: 'absolute', top: 0, left: 0 }}
                                                                            >
                                                                                <path
                                                                                    d={pathData}
                                                                                    stroke={annotation.color}
                                                                                    strokeWidth={2 / scale}
                                                                                    fill="none"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    vectorEffect="non-scaling-stroke"
                                                                                />
                                                                            </svg>
                                                                        );
                                                                    } catch (catchError) {
                                                                        Sentry.captureException(catchError);
                                                                        return null;
                                                                    }
                                                                })()}
                                                                {annotation.annotationType === 'Caret' && (
                                                                    <FaCaretUp size={annotation.height * scale * 0.8} color={annotation.color} />
                                                                )}
                                                                {annotation.annotationType === 'Highlight' && (
                                                                    <div style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        backgroundColor: annotation.color,
                                                                        opacity: 0.3,
                                                                        borderRadius: '2px'
                                                                    }} />
                                                                )}
                                                                {annotation.annotationType === 'Redaction' && (
                                                                    <div style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        backgroundColor: '#000000',
                                                                        borderRadius: '1px'
                                                                    }} />
                                                                )}
                                                                {/* Resize handles for selected annotation */}
                                                                {selectedAnnotation?.annotationId === annotation.annotationId && selectedTool === 'select' && renderResizeHandles()}
                                                            </div>
                                                        );
                                                        })}

                                                    {/* Edit menu for selected annotation */}
                                                    {renderEditMenu()}

                                                    {/* Cursor preview for shape tools */}
                                                    {renderCursorPreview()}

                                        {/* Show drawing path while drawing */}
                                        {isDrawing && drawingPath.length > 0 && (
                                            <svg 
                                                style={{ 
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    pointerEvents: 'none'
                                                }}
                                            >
                                                {selectedTool === 'highlight' ? (
                                                    <rect
                                                        x={Math.min(...drawingPath.map(p => p.x)) * scale}
                                                        y={Math.min(...drawingPath.map(p => p.y)) * scale}
                                                        width={(Math.max(...drawingPath.map(p => p.x)) - Math.min(...drawingPath.map(p => p.x))) * scale}
                                                        height={(Math.max(...drawingPath.map(p => p.y)) - Math.min(...drawingPath.map(p => p.y))) * scale}
                                                        fill={selectedColor}
                                                        fillOpacity={0.3}
                                                    />
                                                ) : (
                                                    <path
                                                        d={drawingPath.map((point, index) => 
                                                            `${index === 0 ? 'M' : 'L'} ${point.x * scale} ${point.y * scale}`
                                                        ).join(' ')}
                                                        stroke={selectedColor}
                                                        strokeWidth={2 * scale}
                                                        fill="none"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                )}
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Sidebar - Audit Trail or Versions */}
                    {showAuditTrail && (
                        <div className="sidebar">
                            <h3>Audit Trail</h3>
                            <div className="audit-list">
                                {auditTrail.length === 0 ? (
                                    <div className="empty-state">No audit entries yet</div>
                                ) : (
                                    auditTrail.map(entry => {
                                        const isClickable = entry.annotationId && entry.pageNumber && !entry.isDeleted;
                                        const isDeleted = entry.isDeleted;
                                        
                                        return (
                                            <div 
                                                key={entry.auditId} 
                                                className={`audit-entry ${isClickable ? 'clickable' : ''} ${isDeleted ? 'deleted' : ''}`}
                                                onClick={() => isClickable && handleAuditEntryClick(entry)}
                                                title={isDeleted ? 'This annotation was deleted' : (isClickable ? 'Click to view annotation' : '')}
                                            >
                                                <div className="audit-user">{entry.userName}</div>
                                                <div className="audit-action">
                                                    {entry.actionDescription}
                                                    {isDeleted && <span className="deleted-badge">Deleted</span>}
                                                </div>
                                                <div className="audit-time">
                                                    {new Date(entry.actionTimestamp).toLocaleString()}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Text Input Modal */}
            {showTextInputModal && (
                <div className="input-modal-overlay" onClick={() => setShowTextInputModal(false)}>
                    <div className="input-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Enter Text</h3>
                        <input
                            type="text"
                            value={textInputValue}
                            onChange={(e) => setTextInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    textInputCallback && textInputCallback(textInputValue);
                                } else if (e.key === 'Escape') {
                                    setShowTextInputModal(false);
                                }
                            }}
                            autoFocus
                            placeholder="Type your text here..."
                        />
                        <div className="input-modal-actions">
                            <button onClick={() => setShowTextInputModal(false)} className="cancel-btn">Cancel</button>
                            <button 
                                onClick={() => textInputCallback && textInputCallback(textInputValue)}
                                className="primary-btn"
                                disabled={!textInputValue.trim()}
                            >
                                Add Text
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Version Creation Modal */}
            {showVersionModal && (
                <div className="input-modal-overlay" onClick={() => setShowVersionModal(false)}>
                    <div className="input-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Create New Version</h3>
                        <p className="modal-description">Creating a new version saves a snapshot of your current annotations.</p>
                        <input
                            type="text"
                            value={versionDescription}
                            onChange={(e) => setVersionDescription(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && versionDescription.trim()) {
                                    handleVersionCreate();
                                } else if (e.key === 'Escape') {
                                    setShowVersionModal(false);
                                }
                            }}
                            autoFocus
                            placeholder="Enter version description..."
                        />
                        <div className="input-modal-actions">
                            <button onClick={() => setShowVersionModal(false)} className="cancel-btn">Cancel</button>
                            <button 
                                onClick={handleVersionCreate} 
                                className="primary-btn"
                                disabled={!versionDescription.trim()}
                            >
                                Create Version
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Signature Creation Modal */}
            {showSignatureModal && (
                <div className="input-modal-overlay" onClick={() => setShowSignatureModal(false)}>
                    <div className="signature-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="signature-modal-header">
                            <h3>Create Your Signature</h3>
                            <button 
                                className="close-btn"
                                onClick={() => setShowSignatureModal(false)}
                                title="Close"
                            >
                                <FaTimes size={16} />
                            </button>
                        </div>
                        <p className="modal-description">
                            Draw your signature below or upload an image. Background will be automatically removed from uploaded images.
                        </p>
                        
                        <div className="signature-canvas-container">
                            <canvas
                                ref={signatureCanvasRef}
                                width={400}
                                height={150}
                                className="signature-canvas"
                                onMouseDown={handleSignatureCanvasMouseDown}
                                onMouseMove={handleSignatureCanvasMouseMove}
                                onMouseUp={handleSignatureCanvasMouseUp}
                                onMouseLeave={handleSignatureCanvasMouseUp}
                            />
                            <div className="signature-canvas-hint">
                                {signatureDrawing.length === 0 && 'Draw your signature here...'}
                            </div>
                        </div>
                        
                        <div className="signature-actions">
                            <button 
                                onClick={clearSignatureCanvas} 
                                className="secondary-btn"
                                title="Clear canvas"
                            >
                                <FaTrash size={12} /> Clear
                            </button>
                            <button 
                                onClick={() => signatureFileInputRef.current?.click()} 
                                className="secondary-btn"
                                title="Upload signature image"
                            >
                                <FaUpload size={12} /> Upload Image
                            </button>
                            <input
                                ref={signatureFileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleSignatureFileUpload}
                                style={{ display: 'none' }}
                            />
                        </div>
                        
                        <div className="input-modal-actions">
                            <button onClick={() => setShowSignatureModal(false)} className="cancel-btn">
                                Cancel
                            </button>
                            <button 
                                onClick={saveSignature} 
                                className="primary-btn"
                                disabled={signatureDrawing.length === 0}
                            >
                                <FaSave size={12} /> Save Signature
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Saving indicator overlay */}
            {isSaving && (
                <div className="saving-overlay">
                    <div className="saving-spinner"></div>
                    <span>Saving...</span>
                </div>
            )}
        </div>
    );

    // Render modal using portal to document body to escape any parent CSS constraints
    return ReactDOM.createPortal(modalContent, document.body);
};

PdfAnnotationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    attachment: PropTypes.shape({
        attachmentId: PropTypes.number.isRequired,
        fileName: PropTypes.string.isRequired,
        downloadUrl: PropTypes.string.isRequired,
        contentType: PropTypes.string
    })
};

export default PdfAnnotationModal;
